import { createApi } from '@reduxjs/toolkit/query/react';
import { dynamicBaseQuery } from '../badRequestHandler';

/* ─── Types ──────────────────────────────────────────────────────────── */

interface AddMerchantArgs {
  name:       string;
  mobile:     string;
  categoryId: string;
}

interface UpdateMerchantArgs extends Partial<AddMerchantArgs> {
  id: string;
}

interface GetMerchantsParams {
  search?:     string;
  status?:     'all' | 'active' | 'inactive';
  categoryId?: string;
  page?:       number;
  limit?:      number;
}

/* ─── API ────────────────────────────────────────────────────────────── */

export const merchantApi = createApi({
  reducerPath: 'merchantApi',
  baseQuery:   dynamicBaseQuery,
  tagTypes:    ['Merchant'] as const,
  endpoints:   (builder) => ({
    getMerchants: builder.query<unknown, GetMerchantsParams | void>({
      query: (params) => {
        const { search, status, categoryId, page, limit } = params || {};
        const qp = new URLSearchParams();
        if (search)          qp.append('search', search);
        if (status)          qp.append('status', status);
        if (categoryId)      qp.append('category', categoryId);
        if (page  != null)   qp.append('page', String(page));
        if (limit != null)   qp.append('limit', String(limit));
        const qs = qp.toString();
        return { url: qs ? `/merchants?${qs}` : `/merchants`, method: 'GET' };
      },
      providesTags: ['Merchant'],
    }),
    getMerchantById: builder.query<unknown, string>({
      query: (id) => ({ url: `/merchants/${id}`, method: 'GET' }),
      providesTags: ['Merchant'],
    }),
    addMerchant: builder.mutation<unknown, AddMerchantArgs>({
      query: (body) => ({ url: `/merchants`, method: 'POST', body }),
      invalidatesTags: ['Merchant'],
    }),
    updateMerchant: builder.mutation<unknown, UpdateMerchantArgs>({
      query: ({ id, ...body }) => ({ url: `/merchants/${id}`, method: 'PUT', body }),
      invalidatesTags: ['Merchant'],
    }),
    toggleMerchantStatus: builder.mutation<unknown, { id: string; isActive: boolean }>({
      query: ({ id, isActive }) => ({ url: `/merchants/${id}/status`, method: 'PATCH', body: { isActive } }),
      invalidatesTags: ['Merchant'],
    }),
    deleteMerchant: builder.mutation<unknown, string>({
      query: (id) => ({ url: `/merchants/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Merchant'],
    }),
    getMerchantCategories: builder.query<unknown, void>({
      query: () => ({ url: `/merchants/categories`, method: 'GET' }),
      providesTags: ['Merchant'],
    }),
    addMerchantCategory: builder.mutation<unknown, { name: string }>({
      query: (body) => ({ url: `/merchants/categories`, method: 'POST', body }),
      invalidatesTags: ['Merchant'],
    }),
  }),
});

export const {
  useGetMerchantsQuery,
  useGetMerchantByIdQuery,
  useAddMerchantMutation,
  useUpdateMerchantMutation,
  useToggleMerchantStatusMutation,
  useDeleteMerchantMutation,
  useGetMerchantCategoriesQuery,
  useAddMerchantCategoryMutation,
} = merchantApi;
